import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/Button';
import {
  ENTERPRISE_DEPARTMENTS,
  ENTERPRISE_PLANTS,
  ROLE_METADATA,
} from '@/config/user-management.config';
import { Role, User, UserCreationInput } from '@/types/domain/user';
import { mockUserManagementService } from '@/services/mock/MockUserManagementService';
import { formatInr } from '@/lib/utils';
import {
  X,
  UserPlus,
  Mail,
  Phone,
  AlertCircle,
  Sparkles,
} from 'lucide-react';
import { showToast } from '@/context/NotificationContext';

interface UserCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUserCreated: (user: User) => void;
  existingUsers: User[];
  currentAdmin?: User | null;
}

export const UserCreateModal: React.FC<UserCreateModalProps> = ({
  isOpen,
  onClose,
  onUserCreated,
  existingUsers,
  currentAdmin,
}) => {
  // Helper to auto-generate next sequential Employee ID
  const computeNextEmployeeId = useCallback(() => {
    const ids = existingUsers
      .map((u) => {
        const match = u.employeeId.match(/EMP-(\d+)/i);
        return match && match[1] ? parseInt(match[1], 10) : null;
      })
      .filter((n): n is number => n !== null);

    const maxId = ids.length > 0 ? Math.max(...ids) : 9120;
    return `EMP-${maxId + 1}`;
  }, [existingUsers]);

  const [name, setName] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('+91 98000 00000');
  const [departmentCode, setDepartmentCode] = useState(ENTERPRISE_DEPARTMENTS[0]!.code);
  const [plantCode, setPlantCode] = useState(ENTERPRISE_PLANTS[0]!.code);
  const [managerId, setManagerId] = useState('usr-2');
  const [role, setRole] = useState<Role>('EMPLOYEE');
  const [approvalAuthorityLimit, setApprovalAuthorityLimit] = useState<number>(10000);

  const [errors, setErrors] = useState<{
    name?: string;
    employeeId?: string;
    email?: string;
    phone?: string;
    approvalAuthorityLimit?: string;
    general?: string;
  }>({});

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize and reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setName('');
      setEmail('');
      setPhone('+91 98000 00000');
      setDepartmentCode(ENTERPRISE_DEPARTMENTS[0]!.code);
      setPlantCode(ENTERPRISE_PLANTS[0]!.code);
      setManagerId('usr-2');
      setRole('EMPLOYEE');
      setApprovalAuthorityLimit(ROLE_METADATA.EMPLOYEE.defaultLimit);
      setEmployeeId(computeNextEmployeeId());
      setErrors({});
    }
  }, [isOpen, computeNextEmployeeId]);

  if (!isOpen) return null;

  // Filter available roles (exclude system-controlled SAP_INTEGRATION)
  const availableRoles = (Object.keys(ROLE_METADATA) as Role[]).filter(
    (r) => !ROLE_METADATA[r].isSystemControlled
  );

  // Filter potential managers
  const potentialManagers = existingUsers.filter(
    (u) => u.role === 'MANAGER' || u.role === 'PROCUREMENT_MANAGER' || u.role === 'MANAGEMENT' || u.role === 'ADMIN'
  );

  const handleRoleChange = (newRole: Role) => {
    setRole(newRole);
    setApprovalAuthorityLimit(ROLE_METADATA[newRole].defaultLimit);
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!name.trim()) {
      newErrors.name = 'Please enter the full name.';
    }

    if (!employeeId.trim()) {
      newErrors.employeeId = 'Please enter the employee ID.';
    }

    if (!email.trim()) {
      newErrors.email = 'Please enter the email address.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = 'Please enter a valid email address.';
    }

    if (!phone.trim()) {
      newErrors.phone = 'Please enter the contact phone number.';
    } else if (!/^\+?[0-9\s-]{10,16}$/.test(phone.trim())) {
      newErrors.phone = 'Please enter a valid contact phone number.';
    }

    if (approvalAuthorityLimit === undefined || approvalAuthorityLimit < 0 || isNaN(approvalAuthorityLimit)) {
      newErrors.approvalAuthorityLimit = 'Please enter a valid approval authority limit.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      const dept = ENTERPRISE_DEPARTMENTS.find((d) => d.code === departmentCode);
      const input: UserCreationInput = {
        name: name.trim(),
        employeeId: employeeId.trim().toUpperCase(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        designation: ROLE_METADATA[role]?.title || 'Specialist',
        departmentCode,
        plantCode,
        companyCode: '1000',
        costCenterCode: dept?.costCenter || 'CC-1010',
        managerId,
        role,
        approvalAuthorityLimit,
        dataScopeOwnership: role === 'MANAGEMENT' || role === 'ADMIN' ? 'ENTERPRISE' : 'DEPARTMENT',
        scopedDepartments: [departmentCode],
        scopedPlants: [plantCode],
      };

      const newUser = await mockUserManagementService.createUser(
        input,
        currentAdmin ? { id: currentAdmin.id, email: currentAdmin.email, role: currentAdmin.role } : undefined
      );

      onUserCreated(newUser);
      showToast(
        'User Provisioned',
        `Account created for ${newUser.name}. Invitation email dispatched to ${newUser.email}.`,
        'success'
      );
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to provision user.';
      if (msg.toLowerCase().includes('email')) {
        setErrors((prev) => ({ ...prev, email: 'Please enter an email address that is not already registered.' }));
      } else if (msg.toLowerCase().includes('employee id')) {
        setErrors((prev) => ({ ...prev, employeeId: 'Please enter an employee ID that is not already assigned.' }));
      } else {
        setErrors((prev) => ({ ...prev, general: msg.endsWith('.') ? msg : `${msg}.` }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 font-poppins">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Professional Enterprise User Provisioning Modal */}
      <div className="relative bg-card border border-border w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden z-10 animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/70 bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
              <UserPlus className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground tracking-tight">
                Provision Enterprise User
              </h2>
              <p className="text-xs text-foreground-secondary mt-0.5">
                Assign identity, corporate phone, hierarchy, and DOA authorization limit.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted text-foreground-muted hover:text-foreground transition-colors cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* 2-Column Form with enlarged font sizes and custom inline errors */}
        <form noValidate onSubmit={handleSubmit} className="p-6 space-y-4">
          {errors.general && (
            <div className="p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive flex items-center gap-2 text-xs font-semibold">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{errors.general}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Full Legal Name */}
            <div className="space-y-1.5">
              <label className="font-semibold text-foreground text-xs uppercase tracking-wider block">
                Full Name <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Vikramaditya Sen"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                }}
                className={`w-full h-10 px-3.5 rounded-xl border text-sm font-medium focus:outline-none transition-all ${errors.name
                  ? 'border-rose-500 bg-rose-50/15 focus:ring-1 focus:ring-rose-500 text-foreground'
                  : 'border-input bg-background text-foreground focus:ring-1 focus:ring-primary'
                  }`}
              />
              {errors.name && (
                <p className="text-xs text-rose-500 font-semibold mt-1 flex items-center gap-1.5 animate-in fade-in duration-150">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  <span>{errors.name}</span>
                </p>
              )}
            </div>

            {/* Corporate Email */}
            <div className="space-y-1.5">
              <label className="font-semibold text-foreground text-xs uppercase tracking-wider block">
                Email <span className="text-rose-500">*</span>
              </label>
              <input
                type="email"
                placeholder="e.g. vikram.sen@procure.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                }}
                className={`w-full h-10 px-3.5 rounded-xl border text-sm font-medium focus:outline-none transition-all ${errors.email
                  ? 'border-rose-500 bg-rose-50/15 focus:ring-1 focus:ring-rose-500 text-foreground'
                  : 'border-input bg-background text-foreground focus:ring-1 focus:ring-primary'
                  }`}
              />
              {errors.email && (
                <p className="text-xs text-rose-500 font-semibold mt-1 flex items-center gap-1.5 animate-in fade-in duration-150">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  <span>{errors.email}</span>
                </p>
              )}
            </div>

            {/* Contact Phone Number */}
            <div className="space-y-1.5">
              <label className="font-semibold text-foreground text-xs uppercase tracking-wider block">
                Contact Phone Number <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
                <input
                  type="text"
                  placeholder="+91 98765 43210"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
                  }}
                  className={`w-full h-10 pl-10 pr-3.5 rounded-xl border text-sm font-medium focus:outline-none transition-all ${errors.phone
                    ? 'border-rose-500 bg-rose-50/15 focus:ring-1 focus:ring-rose-500 text-foreground'
                    : 'border-input bg-background text-foreground focus:ring-1 focus:ring-primary'
                    }`}
                />
              </div>
              {errors.phone && (
                <p className="text-xs text-rose-500 font-semibold mt-1 flex items-center gap-1.5 animate-in fade-in duration-150">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  <span>{errors.phone}</span>
                </p>
              )}
            </div>

            {/* Employee ID (Auto-Generated) */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="font-semibold text-foreground text-xs uppercase tracking-wider">
                  Employee ID <span className="text-rose-500">*</span>
                </label>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-md">
                  <Sparkles className="h-3 w-3" /> Auto-Generated
                </span>
              </div>
              <div className="relative">
                <input
                  type="text"
                  value={employeeId}
                  readOnly
                  className="w-full h-10 px-3.5 rounded-xl border border-border bg-muted/40 font-mono text-sm font-bold text-foreground cursor-not-allowed"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-emerald-600 bg-emerald-500/10 px-2 py-0.5 rounded-md border border-emerald-500/20">
                  Assigned
                </span>
              </div>
              {errors.employeeId && (
                <p className="text-xs text-rose-500 font-semibold mt-1 flex items-center gap-1.5 animate-in fade-in duration-150">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  <span>{errors.employeeId}</span>
                </p>
              )}
            </div>

            {/* Assigned Role */}
            <div className="space-y-1.5">
              <label className="font-semibold text-foreground text-xs uppercase tracking-wider block">
                Assigned Role <span className="text-rose-500">*</span>
              </label>
              <select
                value={role}
                onChange={(e) => handleRoleChange(e.target.value as Role)}
                className="w-full h-10 px-3.5 rounded-xl border border-input bg-background text-foreground text-sm font-medium focus:ring-1 focus:ring-primary focus:outline-none cursor-pointer"
              >
                {availableRoles.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_METADATA[r].title}
                  </option>
                ))}
              </select>
            </div>

            {/* Department */}
            <div className="space-y-1.5">
              <label className="font-semibold text-foreground text-xs uppercase tracking-wider block">
                Department
              </label>
              <select
                value={departmentCode}
                onChange={(e) => setDepartmentCode(e.target.value)}
                className="w-full h-10 px-3.5 rounded-xl border border-input bg-background text-foreground text-sm font-medium focus:ring-1 focus:ring-primary focus:outline-none cursor-pointer"
              >
                {ENTERPRISE_DEPARTMENTS.map((dept) => (
                  <option key={dept.code} value={dept.code}>
                    {dept.name} ({dept.code})
                  </option>
                ))}
              </select>
            </div>

            {/* Plant Facility */}
            <div className="space-y-1.5">
              <label className="font-semibold text-foreground text-xs uppercase tracking-wider block">
                Plant Location
              </label>
              <select
                value={plantCode}
                onChange={(e) => setPlantCode(e.target.value)}
                className="w-full h-10 px-3.5 rounded-xl border border-input bg-background text-foreground text-sm font-medium focus:ring-1 focus:ring-primary focus:outline-none cursor-pointer"
              >
                {ENTERPRISE_PLANTS.map((plant) => (
                  <option key={plant.code} value={plant.code}>
                    {plant.name.split('—')[0]} ({plant.code})
                  </option>
                ))}
              </select>
            </div>

            {/* Reporting Manager */}
            <div className="space-y-1.5">
              <label className="font-semibold text-foreground text-xs uppercase tracking-wider block">
                Reporting Manager
              </label>
              <select
                value={managerId}
                onChange={(e) => setManagerId(e.target.value)}
                className="w-full h-10 px-3.5 rounded-xl border border-input bg-background text-foreground text-sm font-medium focus:ring-1 focus:ring-primary focus:outline-none cursor-pointer"
              >
                {potentialManagers.map((mgr) => (
                  <option key={mgr.id} value={mgr.id}>
                    {mgr.name} ({mgr.role})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* DOA Approval Limit */}
          <div className="space-y-1.5 pt-1">
            <div className="flex items-center justify-between">
              <label className="font-semibold text-foreground text-xs uppercase tracking-wider">
                DOA Approval Authority Limit (₹)
              </label>
              <span className="text-xs text-emerald-600 dark:text-emerald-400 font-bold font-mono">
                {formatInr(approvalAuthorityLimit)}
              </span>
            </div>
            <input
              type="number"
              min="0"
              step="5000"
              value={approvalAuthorityLimit}
              onChange={(e) => {
                setApprovalAuthorityLimit(Number(e.target.value));
                if (errors.approvalAuthorityLimit) {
                  setErrors((prev) => ({ ...prev, approvalAuthorityLimit: undefined }));
                }
              }}
              className={`w-full h-10 px-3.5 rounded-xl border font-mono text-sm font-semibold text-foreground focus:outline-none transition-all ${errors.approvalAuthorityLimit
                ? 'border-rose-500 bg-rose-50/15 focus:ring-1 focus:ring-rose-500'
                : 'border-input bg-background focus:ring-1 focus:ring-primary'
                }`}
            />
            {errors.approvalAuthorityLimit && (
              <p className="text-xs text-rose-500 font-semibold mt-1 flex items-center gap-1.5 animate-in fade-in duration-150">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                <span>{errors.approvalAuthorityLimit}</span>
              </p>
            )}
          </div>

          {/* Zero-Password Policy Inline Note */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-primary/5 border border-primary/15 text-xs text-foreground-secondary">
            <Mail className="h-4 w-4 text-primary shrink-0" />
            <span>
              <strong>Zero-Password Policy:</strong> An onboarding invitation token will be dispatched to the corporate email address.
            </span>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-border/60">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-xl font-bold text-sm h-10 px-4 cursor-pointer"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              isLoading={isSubmitting}
              className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground text-sm h-10 px-5 cursor-pointer shadow-xs"
            >
              <UserPlus className="h-4 w-4 mr-2" /> Provision User
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};
